import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Download, RefreshCw, Trash2, CheckCircle2,
  ArrowLeft, ArrowRight, X, Check, Sparkles, AlertCircle,
  Rocket, ExternalLink, Undo2, ShieldCheck, Loader2, AlertTriangle,
} from 'lucide-react';
import api, { API, getToken } from '../lib/api';
import { formatDateTime } from '../lib/constants';
import { useAuth } from '../lib/auth';
import { computeDocsCompletion, URGENCY_META, PUBLISH_LOCATIONS } from '../lib/documents';
import HelpDot from '../components/HelpDot';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const DOC_HELP_KEYS = {
  privacy_policy: 'documentos_legales',
  cookies_notice: null,
  consent_clause: 'consentimiento',
  dpa: 'dpa',
  arco_notice: 'arco',
};

export default function Documents() {
  const { organization } = useAuth();
  const industry = organization?.industria || 'otro';
  const [types, setTypes] = useState({});
  const [docs, setDocs] = useState([]);
  const [ratCount, setRatCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null); // { doc_type }
  const [implementing, setImplementing] = useState(null); // doc en modal
  const [filter, setFilter] = useState('todos'); // todos | pendiente | implementado

  const load = async () => {
    setLoading(true);
    try {
      const [m, list, acts] = await Promise.all([api.get('/documents/meta'), api.get('/documents'), api.get('/rat/activities')]);
      setTypes(m.data.types); setDocs(list.data); setRatCount(acts.data.length);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDoc = async (id) => {
    const { data } = await api.get(`/documents/${id}`);
    setEditing(data);
  };

  const createWithContext = async (doc_type, answers) => {
    setCreating(null);
    const t = toast.loading('Preparando borrador base…');
    const { site, rat_activity_ids, ...context } = answers;
    try {
      const { data } = await api.post('/documents', {
        doc_type, auto_generate: true, context,
        site_name: site?.name || null, site_url: site?.url || null,
        rat_activity_ids: rat_activity_ids ?? null,
      });
      toast.success('Borrador base creado. Refinando en segundo plano…', { id: t });
      await load();
      openDoc(data.id);
      // Poll cada 6s hasta que generating=false
      const poll = setInterval(async () => {
        try {
          const { data: fresh } = await api.get(`/documents/${data.id}`);
          if (!fresh.generating) {
            clearInterval(poll);
            setEditing(fresh);
            await load();
            toast.success('Borrador refinado');
          }
        } catch (_e) { clearInterval(poll); }
      }, 6000);
      setTimeout(() => clearInterval(poll), 180000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error', { id: t }); }
  };

  const createBlank = async (doc_type) => {
    setCreating(null);
    try {
      const { data } = await api.post('/documents', { doc_type, auto_generate: false });
      toast.success('Documento creado');
      await load();
      openDoc(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const save = async (id, content, change_note) => {
    try {
      const { data } = await api.put(`/documents/${id}`, { content, change_note });
      toast.success('Cambios guardados');
      setEditing(data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const publish = async (id) => {
    const { data } = await api.post(`/documents/${id}/publish`);
    toast.success(`Publicado v${data.published_version}`);
    setEditing(data); await load();
  };

  const implement = async (id, payload) => {
    try {
      const { data } = await api.post(`/documents/${id}/implement`, payload);
      toast.success('Documento marcado como implementado en producción');
      setImplementing(null);
      setEditing(data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const unimplement = async (id) => {
    if (!window.confirm('¿Desmarcar como implementado? El documento volverá a estar "aprobado en Priviax" pero pendiente de publicar.')) return;
    const { data } = await api.delete(`/documents/${id}/implement`);
    toast.success('Marcado como pendiente de implementación');
    setEditing(data); await load();
  };

  const regenerate = async (id) => {
    if (!window.confirm('¿Regenerar el borrador? Se creará una nueva versión.')) return;
    const t = toast.loading('Regenerando en segundo plano…');
    try {
      await api.post(`/documents/${id}/regenerate`);
      toast.success('Regeneración iniciada. Refresque en unos segundos.', { id: t });
      const poll = setInterval(async () => {
        const { data: fresh } = await api.get(`/documents/${id}`);
        if (!fresh.generating) {
          clearInterval(poll);
          setEditing(fresh); await load();
          toast.success(`Nueva versión v${fresh.current_version}`);
        }
      }, 6000);
      setTimeout(() => clearInterval(poll), 180000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error', { id: t }); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    await api.delete(`/documents/${id}`);
    toast.success('Eliminado');
    setEditing(null); await load();
  };

  const download = async (id, slug) => {
    const res = await fetch(`${API}/documents/${id}/export/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${slug}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando documentos…</div>;

  const docTypeKeys = Object.keys(types);
  const completion = computeDocsCompletion(docs, industry, docTypeKeys);
  const groupedAll = docTypeKeys.map((key) => ({
    key,
    meta: types[key],
    docs: docs.filter((d) => d.doc_type === key),
    urgency: completion.grouped.find((g) => g.key === key)?.urgency || 'opcional',
    implemented: completion.grouped.find((g) => g.key === key)?.implemented || false,
  }));
  const grouped = groupedAll.filter((g) => {
    if (filter === 'implementado') return g.implemented;
    if (filter === 'pendiente') return !g.implemented;
    return true;
  });
  const pendingCount = groupedAll.filter((g) => !g.implemented).length;
  const implementedCount = groupedAll.filter((g) => g.implemented).length;

  const requiredMissing = completion.requiredMissing;
  const recommendedMissing = completion.recommendedMissing;
  const { totalNeeded, totalDone, pct } = completion;

  return (
    <MissionShell
      mission={MISSIONS.documentos}
      idPrefix="docs-mission"
      prereq={ratCount === 0 ? { message: 'Tu política de privacidad debe reflejar lo que realmente haces con los datos. Completa primero tu inventario de datos.', to: '/app/rat', cta: 'Ir a Inventario de datos' } : null}
      doHint={requiredMissing.length > 0
        ? `Siguiente acción: te faltan ${requiredMissing.length} documento${requiredMissing.length === 1 ? '' : 's'} obligatorio${requiredMissing.length === 1 ? '' : 's'} por implementar. Genera el borrador, revísalo y márcalo como implementado.`
        : 'Documentos obligatorios implementados. Revisa los recomendados y mantenlos actualizados.'}
      evidence={[
        { label: 'Documentos creados', value: docs.length, tone: docs.length ? 'ok' : 'warn' },
        { label: 'Implementados', value: `${totalDone} de ${totalNeeded}`, tone: totalDone < totalNeeded ? 'warn' : 'ok', detail: 'Publicados o entregados en producción' },
        { label: 'Obligatorios pendientes', value: requiredMissing.length, tone: requiredMissing.length ? 'bad' : 'ok' },
      ]}
      evidenceNote="Cada documento guarda su historial de versiones, fecha de publicación y lugar de implementación."
    >
    <div className="space-y-6" data-testid="documents-page">
      <div className="border-b border-[#E5E7EB] pb-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[15px] font-medium text-[#1C2329]">{docs.length} documento{docs.length === 1 ? '' : 's'}</div>
            <div className="text-[13px] text-[#6B7280]">Documentos legales exigidos o recomendados según la Ley 21.719 y su industria.</div>
          </div>
          <button className="pv-btn-primary flex items-center gap-2" onClick={() => setCreating({ doc_type: 'privacy_policy' })} data-testid="doc-new">
            <Plus size={14} /> Nuevo documento
          </button>
        </div>

        {/* Barra de completitud */}
        <div className="bg-[#FAFBFC] border border-[#E5E7EB] p-4" data-testid="doc-completeness">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2.5">
            <div className="text-[13px] text-[#1C2329]">
              <b>{totalDone}</b> de <b>{totalNeeded}</b> documentos <b>implementados en producción</b>
              {requiredMissing.length > 0 && (
                <span className="ml-2 text-[#DC2626]">· {requiredMissing.length} obligatorio{requiredMissing.length === 1 ? '' : 's'} pendiente{requiredMissing.length === 1 ? '' : 's'}</span>
              )}
              {requiredMissing.length === 0 && recommendedMissing.length > 0 && (
                <span className="ml-2 text-[#92400E]">· {recommendedMissing.length} recomendado{recommendedMissing.length === 1 ? '' : 's'} pendiente{recommendedMissing.length === 1 ? '' : 's'}</span>
              )}
              {requiredMissing.length === 0 && recommendedMissing.length === 0 && (
                <span className="ml-2 text-[#065F46]">· todo al día</span>
              )}
            </div>
            <div className="text-[12px] font-mono text-[#374151] tabular-nums">{pct}%</div>
          </div>
          <div className="h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div className={`h-full transition-all ${
              requiredMissing.length > 0 ? 'bg-[#DC2626]' : recommendedMissing.length > 0 ? 'bg-[#F59E0B]' : 'bg-[#2A9B68]'
            }`} style={{ width: `${pct}%` }} />
          </div>
          {requiredMissing.length > 0 && (
            <div className="mt-3 flex items-start gap-1.5 text-[12px] text-[#991B1B]">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>
                Le faltan documentos <b>obligatorios</b> para su industria: {requiredMissing.map((g) => types[g.key]?.label).join(', ')}.
              </span>
            </div>
          )}
        </div>

        {/* Filtro por estado */}
        <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="doc-filter-chips">
          {[
            { id: 'todos', label: `Todos (${groupedAll.length})` },
            { id: 'pendiente', label: `Pendiente de implementar (${pendingCount})` },
            { id: 'implementado', label: `Implementado (${implementedCount})` },
          ].map((chip) => (
            <button
              key={chip.id}
              onClick={() => setFilter(chip.id)}
              className={`text-[11.5px] px-2.5 py-1 border transition ${
                filter === chip.id
                  ? 'bg-[#143A34] text-white border-[#143A34]'
                  : 'bg-white text-[#374151] border-[#D1D5DB] hover:border-[#9CA3AF]'
              }`}
              data-testid={`doc-filter-${chip.id}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {grouped.map(({ key, meta, docs, urgency, implemented }) => {
          const um = URGENCY_META[urgency];
          const published = docs.some((d) => d.status === 'publicado');
          const implementedDoc = docs.find((d) => d.implementation);
          return (
            <div key={key} className="border border-[#E5E7EB] bg-white p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[14px] font-semibold text-[#1C2329] leading-snug flex items-center gap-1.5">
                  {meta.label}
                  {DOC_HELP_KEYS[key] && <HelpDot termKey={DOC_HELP_KEYS[key]} size="sm" />}
                </div>
                <span
                  className="text-[10.5px] font-medium px-1.5 py-0.5 border shrink-0"
                  style={{ color: um.color, backgroundColor: um.bg, borderColor: um.border }}
                  data-testid={`doc-urgency-${key}`}
                >
                  {um.label}
                </span>
              </div>
              <div className="text-[11.5px] text-[#6B7280] leading-snug">
                <span className="text-[#9CA3AF]">Dónde:</span> {PUBLISH_LOCATIONS[key] || '—'}
              </div>

              {docs.length === 0 ? (
                <div className="mt-3 text-[12px] text-[#6B7280]">Sin documentos aún.</div>
              ) : (
                <div className="mt-3 space-y-2">
                  {docs.map((d) => {
                    const impl = d.implementation;
                    return (
                      <button key={d.id} onClick={() => openDoc(d.id)} className="w-full text-left p-3 border border-[#E5E7EB] hover:border-[#2A9B68]/50" data-testid={`doc-${d.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] font-medium text-[#1C2329] truncate">{d.title}</span>
                          <span className="text-[10px] text-[#6B7280] font-mono ml-2 shrink-0">v{d.current_version}</span>
                        </div>
                        {d.site_name && (
                          <div className="mt-0.5 text-[11px] text-[#374151]" data-testid={`doc-site-${d.id}`}>Sitio: {d.site_name}{d.site_url ? ` · ${d.site_url}` : ''}</div>
                        )}
                        <div className="mt-1 flex items-center justify-between text-[11px]">
                          {impl ? (
                            <span className="inline-flex items-center gap-1 text-[#065F46]">
                              <Rocket size={10} strokeWidth={2} /> Implementado en producción
                            </span>
                          ) : d.status === 'publicado' ? (
                            <span className="text-[#92400E]">◐ Aprobado en Priviax · pendiente implementar</span>
                          ) : (
                            <span className="text-[#6B7280]">○ Borrador</span>
                          )}
                          <span className="text-[#9CA3AF]">{formatDateTime(d.updated_at)}</span>
                        </div>
                        {impl?.url && (
                          <div className="mt-1 text-[10.5px] text-[#065F46] font-mono truncate">{impl.url}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {!implemented && urgency === 'obligatorio' && (
                <div className="mt-3 text-[11px] text-[#991B1B] flex items-center gap-1">
                  <AlertCircle size={11} /> Pendiente de implementar
                </div>
              )}
              {implementedDoc?.implementation?.url && (
                <a href={implementedDoc.implementation.url} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-2 text-[11px] text-[#2A9B68] hover:underline inline-flex items-center gap-1">
                  <ExternalLink size={10} /> Ver publicado
                </a>
              )}

              <button className="mt-auto pt-3 text-[12px] text-[#2A9B68] hover:text-[#228256] text-left" onClick={() => setCreating({ doc_type: key })} data-testid={`doc-create-${key}`}>
                + Crear {meta.label.toLowerCase()}
              </button>
            </div>
          );
        })}
      </div>

      {creating && (
        <DocumentWizard
          docType={creating.doc_type}
          types={types}
          onCancel={() => setCreating(null)}
          onGenerate={(ctx) => createWithContext(creating.doc_type, ctx)}
          onBlank={() => createBlank(creating.doc_type)}
          onChangeType={(k) => setCreating({ doc_type: k })}
        />
      )}

      {editing && (
        <DocumentEditor doc={editing} types={types} onClose={() => setEditing(null)}
          onSave={save} onPublish={publish} onRegenerate={regenerate} onDelete={del} onDownload={download} />
      )}
    </div>
    </MissionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Preguntas contextuales por tipo de documento                      */
/* ------------------------------------------------------------------ */

const COOKIE_CATEGORIES = [
  { value: 'necesarias',    label: 'Estrictamente necesarias', example: 'Sesión, carrito de compras' },
  { value: 'funcionales',   label: 'Funcionales',              example: 'Preferencias, idioma, chat' },
  { value: 'analiticas',    label: 'Analíticas',               example: 'Google Analytics, Hotjar' },
  { value: 'publicitarias', label: 'Publicitarias / Marketing', example: 'Meta Pixel, Google Ads' },
];

const SITE_Q = {
  key: 'site',
  title: '¿Para qué sitio o producto es este documento?',
  hint: 'El responsable legal sigue siendo tu empresa, pero el documento nombrará este producto y su URL.',
  type: 'site',
  required: true,
};

const DOC_QUESTIONS = {
  privacy_policy: [
    SITE_Q,
    {
      key: 'rat_activity_ids',
      title: '¿Qué actividades de tu inventario aplican a este sitio?',
      hint: 'La política se redacta con las finalidades, bases legales, plazos y proveedores reales de estas actividades.',
      type: 'activities',
    },
    {
      key: 'arco_channel',
      title: '¿Cómo pueden ejercer sus derechos las personas?',
      hint: 'Correo electrónico, formulario, portal, etc. Por ley debes responder en 15 días hábiles.',
      type: 'text',
      placeholder: 'Ej: privacidad@miempresa.cl o formulario en /derechos',
      required: true,
    },
    {
      key: 'dpo_email',
      title: '¿Hay un Encargado de Protección de Datos (DPO)?',
      hint: 'Si tienes uno, indica su correo. Si no, deja en blanco.',
      type: 'text',
      placeholder: 'dpo@miempresa.cl (opcional)',
    },
    {
      key: 'intl_transfers',
      title: '¿Los datos salen de Chile en algún momento?',
      hint: 'Ejemplos: uso de Google Workspace, AWS, HubSpot, Mailchimp, etc.',
      type: 'text',
      placeholder: 'Ej: Google Workspace (EE.UU.), HubSpot (EE.UU.) — con DPA firmado',
    },
    {
      key: 'cookies_used',
      title: '¿Qué tipo de cookies o rastreadores usa tu sitio?',
      hint: 'Marca todas las que apliquen.',
      type: 'multi',
      options: COOKIE_CATEGORIES,
    },
  ],
  cookies_notice: [
    SITE_Q,
    {
      key: 'cookies_used',
      title: '¿Qué categorías de cookies usas?',
      hint: 'Marca todas las que apliquen.',
      type: 'multi',
      options: COOKIE_CATEGORIES,
      required: true,
    },
    {
      key: 'third_party_tools',
      title: '¿Qué herramientas de terceros están instaladas?',
      hint: 'Analytics, píxeles publicitarios, chats, mapas, etc.',
      type: 'text',
      placeholder: 'Ej: Google Analytics 4, Meta Pixel, Google Maps',
    },
  ],
  consent_clause: [
    SITE_Q,
    {
      key: 'consent_context',
      title: '¿En qué contexto se usará esta cláusula?',
      hint: 'Formulario web, papel, onboarding, etc.',
      type: 'text',
      placeholder: 'Ej: Formulario de registro en el sitio web',
      required: true,
    },
    {
      key: 'consent_purposes',
      title: '¿Para qué finalidades pedirás consentimiento?',
      hint: 'Ejemplos separados por coma. Idealmente separas cada finalidad.',
      type: 'text',
      placeholder: 'Ej: envío de newsletter, ofertas comerciales de terceros',
      required: true,
    },
  ],
  dpa: [
    {
      key: 'provider_name',
      title: '¿Con qué proveedor firmarás este DPA?',
      hint: 'Nombre legal del encargado del tratamiento.',
      type: 'text',
      placeholder: 'Ej: NubeChile SpA',
      required: true,
    },
    {
      key: 'provider_service',
      title: '¿Qué servicio te presta ese proveedor?',
      hint: 'Describe brevemente el servicio y qué datos maneja por ti.',
      type: 'text',
      placeholder: 'Ej: Hosting y backups de la base de datos de clientes',
      required: true,
    },
    {
      key: 'subprocessors',
      title: '¿Puede subcontratar a otros proveedores?',
      hint: 'Si autorizas subencargados, indica cuáles. Si no, deja en blanco.',
      type: 'text',
      placeholder: 'Ej: AWS (infraestructura), Datadog (monitoreo) — con notificación previa',
    },
    {
      key: 'rat_activity_ids',
      title: '¿Qué actividades de tu inventario maneja este proveedor?',
      hint: 'El acuerdo describirá los datos, titulares y finalidades reales de estas actividades.',
      type: 'activities',
    },
  ],
  arco_notice: [
    SITE_Q,
    {
      key: 'arco_channel',
      title: '¿Cuál es el canal público para solicitudes ARCO+?',
      hint: 'Debes tener uno visible y con respuesta en 15 días hábiles.',
      type: 'text',
      placeholder: 'Ej: privacidad@miempresa.cl o /derechos-arco',
      required: true,
    },
    {
      key: 'arco_portal_url',
      title: '¿Tienes portal de solicitudes ARCO+? (opcional)',
      hint: 'URL si tienes formulario dedicado. Se puede activar más adelante con Priviax.',
      type: 'text',
      placeholder: 'https://miempresa.cl/portal-arco',
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Wizard de generación de documentos                                 */
/* ------------------------------------------------------------------ */

function DocumentWizard({ docType, types, onCancel, onGenerate, onBlank, onChangeType }) {
  const { organization } = useAuth();
  const questions = DOC_QUESTIONS[docType] || [];
  const [step, setStep] = useState(0); // 0 = elegir tipo, 1..n = preguntas, n+1 = confirmar
  const [answers, setAnswers] = useState({});
  const [sites, setSites] = useState([]);
  const [acts, setActs] = useState(null);

  useEffect(() => {
    api.get('/cmp/configs').then(({ data }) => {
      const list = (data.configs || []).map((c) => ({ name: c.name || 'Sitio principal', url: c.domain || '' }));
      if (organization?.website && !list.some((s) => s.url && organization.website.includes(s.url))) {
        list.unshift({ name: organization.razon_social, url: organization.website });
      }
      setSites(list);
    }).catch(() => setSites(organization?.website ? [{ name: organization.razon_social, url: organization.website }] : []));
    api.get('/rat/activities').then(({ data }) => setActs(data)).catch(() => setActs([]));
  }, [organization?.id]);

  useEffect(() => {
    if (acts && answers.rat_activity_ids === undefined && questions.some((q) => q.type === 'activities')) {
      setAnswers((a) => ({ ...a, rat_activity_ids: acts.map((x) => x.id) }));
    }
  }, [acts, docType]);

  const totalSteps = 1 + questions.length + 1; // tipo + preguntas + resumen
  const currentQuestion = questions[step - 1];
  const isTypeStep = step === 0;
  const isSummary = step === totalSteps - 1;

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const canNext = useMemo(() => {
    if (isTypeStep) return !!docType;
    if (isSummary) return true;
    if (!currentQuestion) return true;
    if (!currentQuestion.required) return true;
    const v = answers[currentQuestion.key];
    if (currentQuestion.type === 'site') return !!v?.name?.trim();
    if (currentQuestion.type === 'multi') return Array.isArray(v) && v.length > 0;
    return typeof v === 'string' && v.trim().length > 0;
  }, [isTypeStep, isSummary, currentQuestion, answers, docType]);

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="bg-white border border-[#E5E7EB] max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="doc-wizard"
      >
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329]">
              Nuevo documento — {types[docType]?.label}
            </div>
            <div className="text-[12px] text-[#6B7280] mt-0.5">
              Respondemos algunas preguntas para adaptar el borrador a tu empresa.
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 py-2.5 border-b border-[#E5E7EB] bg-[#FAFBFC] flex items-center gap-1.5" data-testid="doc-wizard-progress">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${
              i < step ? 'bg-[#143A34]' : i === step ? 'bg-[#143A34]/50' : 'bg-[#E5E7EB]'
            }`} />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isTypeStep && (
            <div className="space-y-4">
              <div>
                <div className="text-[10.5px] tracking-wider text-[#6B7280] font-medium">Paso 1 de {totalSteps}</div>
                <h3 className="text-[19px] font-semibold text-[#1C2329] mt-1 leading-snug">¿Qué documento quieres crear?</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(types).map(([k, meta]) => {
                  const active = docType === k;
                  return (
                    <button key={k} type="button" onClick={() => onChangeType(k)}
                      className={`text-left border p-3 transition-colors ${
                        active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
                      }`}
                      data-testid={`doc-type-${k}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-[13.5px] font-medium text-[#1C2329]">{meta.label}</div>
                          <div className="text-[11.5px] text-[#6B7280] mt-0.5">{DOC_DESC[k] || ''}</div>
                        </div>
                        <div className={`shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'}`}>
                          {active && <Check size={10} strokeWidth={4} className="text-white m-auto mt-[1px]" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isTypeStep && !isSummary && currentQuestion && (
            <div className="space-y-4">
              <div>
                <div className="text-[10.5px] tracking-wider text-[#6B7280] font-medium">Paso {step + 1} de {totalSteps}</div>
                <h3 className="text-[19px] font-semibold text-[#1C2329] mt-1 leading-snug">{currentQuestion.title}</h3>
                <p className="text-[12.5px] text-[#6B7280] mt-1">{currentQuestion.hint}</p>
              </div>

              {currentQuestion.type === 'text' && (
                <input
                  className="pv-input"
                  placeholder={currentQuestion.placeholder}
                  value={answers[currentQuestion.key] || ''}
                  onChange={(e) => setAnswer(currentQuestion.key, e.target.value)}
                  data-testid={`doc-q-${currentQuestion.key}`}
                  autoFocus
                />
              )}

              {currentQuestion.type === 'site' && (
                <SitePicker sites={sites} value={answers.site} onChange={(v) => setAnswer('site', v)} />
              )}

              {currentQuestion.type === 'activities' && (
                <ActivitiesPicker acts={acts} value={answers.rat_activity_ids || []} onChange={(v) => setAnswer('rat_activity_ids', v)} />
              )}

              {currentQuestion.type === 'multi' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {currentQuestion.options.map((opt) => {
                    const list = answers[currentQuestion.key] || [];
                    const active = list.includes(opt.value);
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => {
                          const next = active ? list.filter((x) => x !== opt.value) : [...list, opt.value];
                          setAnswer(currentQuestion.key, next);
                        }}
                        className={`text-left border p-2.5 flex items-start gap-2 transition-colors ${
                          active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'
                        }`}
                        data-testid={`doc-q-${currentQuestion.key}-${opt.value}`}>
                        <div className={`shrink-0 w-4 h-4 border-2 mt-0.5 flex items-center justify-center ${
                          active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'
                        }`}>
                          {active && <Check size={10} strokeWidth={4} className="text-white" />}
                        </div>
                        <div>
                          <div className="text-[13px] font-medium text-[#1C2329]">{opt.label}</div>
                          <div className="text-[11.5px] text-[#6B7280] mt-0.5">{opt.example}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isSummary && (
            <div className="space-y-5">
              <div>
                <div className="text-[10.5px] tracking-wider text-[#6B7280] font-medium">Paso {totalSteps} de {totalSteps}</div>
                <h3 className="text-[19px] font-semibold text-[#1C2329] mt-1 leading-snug">Todo listo</h3>
                <p className="text-[13px] text-[#6B7280] mt-1">Con estos datos redactaremos un borrador adaptado. Toma cerca de 90 segundos; puedes seguir usando la app mientras tanto.</p>
              </div>

              <div className="border border-[#E5E7EB] bg-[#FAFBFC] divide-y divide-[#E5E7EB] text-[12.5px]" data-testid="doc-wizard-summary">
                <SummaryRow label="Tipo" value={types[docType]?.label} onEdit={() => setStep(0)} />
                {questions.map((q, i) => (
                  <SummaryRow
                    key={q.key}
                    label={q.title.replace(/\?$/, '').slice(0, 40)}
                    value={formatAnswer(answers[q.key], q, acts)}
                    onEdit={() => setStep(i + 1)}
                  />
                ))}
              </div>

              <div className="border border-[#143A34]/20 bg-[#F0FDF4] px-3 py-2.5 flex items-start gap-2 text-[12.5px] text-[#065F46]">
                <Sparkles size={14} className="mt-0.5 shrink-0" />
                <span>Con esta información generaremos un borrador basado en la Ley N.º 21.719 y adaptado a tu empresa. Podrás editarlo antes de publicarlo.</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] flex items-center justify-between bg-white">
          <button className="pv-btn-secondary flex items-center gap-1.5 text-[13px]"
            onClick={() => step === 0 ? onCancel() : setStep(step - 1)}
            data-testid="doc-wizard-back">
            <ArrowLeft size={13} /> {step === 0 ? 'Cancelar' : 'Atrás'}
          </button>

          <div className="flex items-center gap-2">
            {isSummary ? (
              <>
                <button className="pv-btn-secondary text-[13px]" onClick={onBlank} data-testid="doc-wizard-blank">
                  Empezar en blanco
                </button>
                <button className="pv-btn-primary flex items-center gap-1.5" onClick={() => onGenerate(answers)} data-testid="doc-wizard-generate">
                  <Sparkles size={14} /> Generar borrador
                </button>
              </>
            ) : (
              <button className="pv-btn-primary flex items-center gap-1.5" disabled={!canNext}
                onClick={() => setStep(step + 1)} data-testid="doc-wizard-next">
                Continuar <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const DOC_DESC = {
  privacy_policy: 'Texto público para el sitio web con qué datos tratas y por qué.',
  cookies_notice: 'Aviso obligatorio en el banner de cookies.',
  consent_clause: 'Casilla y texto para pedir permiso en formularios.',
  dpa:            'Contrato con un proveedor que trata datos por ti (hosting, mailing, etc.).',
  arco_notice:    'Cómo las personas pueden pedir ver, corregir o eliminar sus datos.',
};

function SitePicker({ sites, value, onChange }) {
  const [custom, setCustom] = useState(false);
  const isSel = (s) => !custom && value?.name === s.name && (value?.url || '') === (s.url || '');
  return (
    <div className="space-y-2" data-testid="doc-q-site">
      {sites.map((s) => (
        <button key={`${s.name}-${s.url}`} type="button" onClick={() => { setCustom(false); onChange(s); }}
          className={`w-full text-left border p-2.5 transition-colors ${isSel(s) ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'}`}
          data-testid={`doc-site-option-${s.name}`}>
          <div className="text-[13px] font-medium text-[#1C2329]">{s.name}</div>
          <div className="text-[11.5px] text-[#6B7280] font-mono">{s.url || 'sin URL'}</div>
        </button>
      ))}
      <button type="button" onClick={() => { setCustom(true); onChange({ name: '', url: '' }); }}
        className={`w-full text-left border p-2.5 text-[13px] ${custom ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-dashed border-[#D1D5DB] text-[#374151]'}`}
        data-testid="doc-site-other">
        + Otro sitio o producto
      </button>
      {custom && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className="pv-input" placeholder="Nombre del producto" value={value?.name || ''}
            onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="doc-site-name" autoFocus />
          <input className="pv-input" placeholder="https://producto.cl" value={value?.url || ''}
            onChange={(e) => onChange({ ...value, url: e.target.value })} data-testid="doc-site-url" />
        </div>
      )}
    </div>
  );
}

function ActivitiesPicker({ acts, value, onChange }) {
  if (acts === null) return <div className="text-[12.5px] text-[#6B7280]">Cargando inventario…</div>;
  if (acts.length === 0) {
    return (
      <div className="border border-[#F59E0B]/40 bg-[#FFFBEB] px-3 py-2.5 text-[12.5px] text-[#92400E]" data-testid="doc-acts-empty">
        Tu inventario está vacío. Completa primero tu <a href="/app/rat" className="underline font-medium">Inventario de datos</a> para que el documento refleje lo que realmente haces. Si continúas, se redactará un borrador genérico.
      </div>
    );
  }
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="space-y-1.5" data-testid="doc-q-activities">
      <div className="flex justify-end gap-3 text-[11.5px]">
        <button type="button" className="text-[#143A34] hover:underline" onClick={() => onChange(acts.map((a) => a.id))}>Todas</button>
        <button type="button" className="text-[#6B7280] hover:underline" onClick={() => onChange([])}>Ninguna</button>
      </div>
      {acts.map((a) => {
        const active = value.includes(a.id);
        return (
          <button key={a.id} type="button" onClick={() => toggle(a.id)}
            className={`w-full text-left border p-2.5 flex items-start gap-2 transition-colors ${active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'}`}
            data-testid={`doc-act-${a.id}`}>
            <div className={`shrink-0 w-4 h-4 border-2 mt-0.5 flex items-center justify-center ${active ? 'border-[#143A34] bg-[#143A34]' : 'border-[#D1D5DB]'}`}>
              {active && <Check size={10} strokeWidth={4} className="text-white" />}
            </div>
            <div>
              <div className="text-[13px] font-medium text-[#1C2329]">{a.name}</div>
              <div className="text-[11.5px] text-[#6B7280] mt-0.5">{a.area} · {a.purpose}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function formatAnswer(v, q, acts) {
  if (q?.type === 'site') return v?.name ? `${v.name}${v.url ? ` · ${v.url}` : ''}` : '—';
  if (q?.type === 'activities') {
    if (!acts?.length) return 'Inventario vacío';
    return `${(v || []).length} de ${acts.length} actividades`;
  }
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    return v.map((x) => COOKIE_CATEGORIES.find((c) => c.value === x)?.label || x).join(', ');
  }
  return String(v);
}

function SummaryRow({ label, value, onEdit }) {
  return (
    <div className="grid grid-cols-[160px_1fr_auto] gap-3 px-3 py-2 items-start">
      <div className="text-[#6B7280]">{label}</div>
      <div className="text-[#1C2329]">{value}</div>
      <button onClick={onEdit} className="text-[11.5px] text-[#143A34] hover:underline">Editar</button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Editor de documento (sin cambios funcionales)                     */
/* ------------------------------------------------------------------ */

function DocumentEditor({ doc, types, onClose, onSave, onPublish, onRegenerate, onDelete, onDownload, onImplement, onUnimplement }) {
  const [content, setContent] = useState(doc.content);
  const [note, setNote] = useState('');
  const [review, setReview] = useState(
    doc.last_review?.version === doc.current_version ? doc.last_review : null,
  );
  const dirty = content !== doc.content;
  const isPublished = !!doc.published_version;
  const isImplemented = !!doc.implementation;
  const criticalCount = [...(review?.auto || []), ...(review?.ai || [])].filter((f) => f.level === 'critical').length;

  const handleImplement = () => {
    if (!review) {
      if (!window.confirm('Este documento aún no ha sido revisado. ¿Marcar como implementado de todas formas?')) return;
    } else if (criticalCount > 0) {
      if (!window.confirm(`La revisión encontró ${criticalCount} hallazgo${criticalCount === 1 ? '' : 's'} crítico${criticalCount === 1 ? '' : 's'} sin resolver. ¿Marcar como implementado de todas formas?`)) return;
    }
    onImplement();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#E5E7EB] max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="doc-editor">
        <div className="p-5 border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329]">{doc.title}</div>
            <div className="text-[12px] text-[#6B7280] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{types[doc.doc_type]?.label} · v{doc.current_version}</span>
              {isPublished && !isImplemented && (
                <span className="text-[#92400E]">◐ Aprobado en Priviax · pendiente implementar</span>
              )}
              {isImplemented && (
                <span className="text-[#065F46] inline-flex items-center gap-1">
                  <Rocket size={11} /> Implementado en producción
                </span>
              )}
              {doc.generating && <span className="text-[#2563EB]">● refinando…</span>}
            </div>
            {isImplemented && (
              <div className="mt-1.5 text-[11.5px] text-[#374151] flex flex-wrap items-center gap-x-3 gap-y-0.5">
                {doc.implementation.url && (
                  <a href={doc.implementation.url} target="_blank" rel="noopener noreferrer"
                    className="text-[#2A9B68] hover:underline font-mono inline-flex items-center gap-1">
                    {doc.implementation.url} <ExternalLink size={10} />
                  </a>
                )}
                {doc.implementation.location && !doc.implementation.url && (
                  <span className="font-mono text-[#374151]">{doc.implementation.location}</span>
                )}
                <span className="text-[#9CA3AF]">
                  · confirmado por {doc.implementation.confirmed_by_email} · {formatDateTime(doc.implementation.deployed_at)}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="pv-btn-secondary text-[13px] flex items-center gap-1" onClick={() => onDownload(doc.id, types[doc.doc_type]?.slug)} data-testid="doc-download"><Download size={13} /> PDF</button>
            <button className="pv-btn-secondary text-[13px] flex items-center gap-1" onClick={() => onRegenerate(doc.id)} data-testid="doc-regenerate"><RefreshCw size={13} /> Regenerar</button>
            {doc.current_version > (doc.published_version || 0) && (
              <button className="pv-btn-secondary text-[13px] flex items-center gap-1" onClick={() => onPublish(doc.id)} data-testid="doc-publish"><CheckCircle2 size={13} /> Aprobar v{doc.current_version}</button>
            )}
            {isPublished && !isImplemented && (
              <button
                className="text-[13px] flex items-center gap-1 px-3 py-1.5 bg-[#143A34] text-white hover:bg-[#0F2A26] rounded-sm"
                onClick={handleImplement} data-testid="doc-implement">
                <Rocket size={13} /> Marcar como implementado
              </button>
            )}
            {isImplemented && (
              <button className="pv-btn-secondary text-[13px] flex items-center gap-1 text-[#92400E]"
                onClick={() => onUnimplement(doc.id)} data-testid="doc-unimplement">
                <Undo2 size={13} /> Desmarcar
              </button>
            )}
            <button className="text-[#DC2626] hover:bg-[#FEF2F2] p-2 rounded" onClick={() => onDelete(doc.id)}><Trash2 size={14} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <ReviewPanel doc={doc} dirty={dirty} review={review} setReview={setReview} />
          <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6B7280] mb-1.5">Editor · markdown</label>
            <textarea className="pv-input font-mono text-[12.5px]" rows={22} value={content} onChange={(e) => setContent(e.target.value)} data-testid="doc-content" />
          </div>
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6B7280] mb-1.5">Vista previa</label>
            <div className="border border-[#E5E7EB] p-4 h-[500px] overflow-y-auto text-[13px] leading-relaxed whitespace-pre-line" data-testid="doc-preview">
              {content}
            </div>
          </div>
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
          <input className="pv-input max-w-xs" placeholder="Nota del cambio (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2">
            <button className="pv-btn-secondary" onClick={onClose}>Cerrar</button>
            <button className="pv-btn-primary" disabled={!dirty} onClick={() => onSave(doc.id, content, note)} data-testid="doc-save">Guardar cambios</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Revisión del documento (automática + IA) ---------- */

const LEVEL_META = {
  critical: { icon: AlertCircle, color: '#DC2626', bg: '#FEF2F2', label: 'Crítico' },
  warning: { icon: AlertTriangle, color: '#B45309', bg: '#FFFBEB', label: 'Advertencia' },
  ok: { icon: CheckCircle2, color: '#2A9B68', bg: '#F0FDF4', label: 'OK' },
};

function ReviewPanel({ doc, dirty, review, setReview }) {
  const [loadingAuto, setLoadingAuto] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);

  const run = async (ai) => {
    if (dirty) { toast.error('Guarda los cambios antes de revisar.'); return; }
    ai ? setLoadingAi(true) : setLoadingAuto(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/review${ai ? '?ai=true' : ''}`, null, { timeout: 120000 });
      setReview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo revisar'); }
    finally { ai ? setLoadingAi(false) : setLoadingAuto(false); }
  };

  const all = [...(review?.auto || []), ...(review?.ai || [])];
  const crit = all.filter((f) => f.level === 'critical').length;
  const warn = all.filter((f) => f.level === 'warning').length;

  return (
    <div className="border border-[#E5E7EB] bg-[#FAFBFC]" data-testid="doc-review-panel">
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-medium text-[#1C2329]">
          <ShieldCheck size={14} className="text-[#2A9B68]" />
          Revisión del documento
          {review && (
            <span className="text-[12px] font-normal text-[#6B7280]" data-testid="doc-review-summary">
              · v{review.version} · {crit} crítico{crit === 1 ? '' : 's'} · {warn} advertencia{warn === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button className="pv-btn-secondary text-[12.5px] flex items-center gap-1" onClick={() => run(false)} disabled={loadingAuto || loadingAi} data-testid="doc-review-auto">
            {loadingAuto ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Revisar documento
          </button>
          <button className="pv-btn-secondary text-[12.5px] flex items-center gap-1" onClick={() => run(true)} disabled={loadingAuto || loadingAi} data-testid="doc-review-ai">
            {loadingAi ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {loadingAi ? 'Revisando con IA…' : 'Revisión legal con IA'}
          </button>
        </div>
      </div>
      {review?.auto && <FindingList title="Revisión automática" items={review.auto} testid="doc-review-auto-list" />}
      {review?.ai && <FindingList title="Revisión legal con IA (orientativa, no reemplaza a un abogado)" items={review.ai} testid="doc-review-ai-list" />}
    </div>
  );
}

function FindingList({ title, items, testid }) {
  return (
    <div className="border-t border-[#E5E7EB] px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-medium text-[#6B7280] mb-2">{title}</div>
      <ul className="space-y-1.5">
        {items.map((f, i) => {
          const m = LEVEL_META[f.level] || LEVEL_META.warning;
          const Icon = m.icon;
          return (
            <li key={i} className="flex items-start gap-2 px-2.5 py-2 text-[12.5px]" style={{ background: m.bg }} data-testid={`finding-${f.level}`}>
              <Icon size={13} className="mt-0.5 shrink-0" style={{ color: m.color }} />
              <div>
                <span className="font-medium text-[#1C2329]">{f.title}</span>
                {f.detail && <span className="text-[#374151]"> — {f.detail}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// end
